import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { scrollToPricing } from '../lib/stripe';

const FloatingOrb = ({ className, delay = 0 }) => (
  <motion.div
    className={`absolute rounded-full blur-[100px] pointer-events-none ${className}`}
    animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
    transition={{ duration: 6 + delay, repeat: Infinity, ease: 'easeInOut', delay }}
  />
);

const Hero = () => {
  const { language } = useLanguage();
  const text = t[language].hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const containerVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
  };

  return (
    <section ref={ref} className="relative pt-12 pb-16 sm:pt-24 sm:pb-24 overflow-hidden min-h-[90vh] flex items-center">
      <FloatingOrb className="w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-orange-600/20 top-1/4 -left-32" delay={0} />
      <FloatingOrb className="w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-pink-600/15 top-1/3 -right-24" delay={2} />
      <FloatingOrb className="w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-purple-600/20 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" delay={1} />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px]" />
      
      <motion.div style={{ y, opacity }} className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div className="max-w-5xl mx-auto text-center" variants={containerVariants} initial="hidden" animate="visible">
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-orange-500/20 text-orange-400 text-xs sm:text-sm font-bold mb-6 sm:mb-8">
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
            {text.trial}
          </motion.div>
          
          <motion.h1 variants={itemVariants} className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white tracking-tighter leading-[1.1] sm:leading-[1.05] mb-6">
            {text.title}{' '}
            <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 pb-2">
              {text.titleHighlight}
            </span>
          </motion.h1>
          
          <motion.p variants={itemVariants} className="text-base sm:text-xl text-gray-300 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
            {text.subtitle}
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-center gap-3 mb-10">
            {text.features.map((feat, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 glass rounded-xl text-white/90 font-bold text-xs sm:text-sm border border-white/5">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-orange-400 text-sm" />
                {feat}
              </div>
            ))}
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <motion.button
              onClick={scrollToPricing}
              className="relative px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-orange-500 to-purple-600 text-white rounded-full font-black text-lg sm:text-xl overflow-hidden group flex items-center gap-3 mx-auto shadow-[0_0_40px_rgba(249,115,22,0.3)] hover:shadow-[0_0_60px_rgba(249,115,22,0.5)] transition-shadow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              <span className="relative z-10">{text.cta}</span>
              <SafeIcon icon={FiIcons.FiArrowRight} className="relative z-10 text-xl group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
      
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0f] to-transparent" />
    </section>
  );
};

export default Hero;